// SpeakRoots-specific analytics events matching the exact requirements
import { trackEvent } from './analytics';

// A. Onboarding & Acquisition Funnel
export const trackOnboardingStarted = () => {
    trackEvent('onboarding_started', {
        timestamp: new Date().toISOString()
    });
};

export const trackLanguageSelected = (language) => {
    trackEvent('language_selected', {
        language, // 'Twi' | 'Yoruba'
        timestamp: new Date().toISOString()
    });
};

export const trackTrialActivated = () => {
    trackEvent('trial_activated', {
        timestamp: new Date().toISOString()
    });
};

export const trackProfileCreated = (name, ageGroup, language) => {
    trackEvent('profile_created', {
        name,
        age_group: ageGroup,
        language,
        timestamp: new Date().toISOString()
    });
};

// B. Core Learning Loop
export const trackLessonStarted = (lessonId, unitNumber) => {
    trackEvent('lesson_started', {
        lesson_id: lessonId, // e.g., 'twi_m1_lesson1_greetings'
        unit_number: unitNumber,
        timestamp: new Date().toISOString()
    });
};

export const trackLessonCompleted = (lessonId, unitNumber) => {
    trackEvent('lesson_completed', {
        lesson_id: lessonId, // e.g., 'twi_m1_lesson1_greetings'
        unit_number: unitNumber,
        timestamp: new Date().toISOString()
    });
};

export const trackActivityStarted = (activityId, activityType, unitNumber) => {
    trackEvent('activity_started', {
        activity_id: activityId, // e.g., 'twi_m1_activity_scramble1'
        activity_type: activityType, // e.g., 'wordScramble'
        unit_number: unitNumber,
        timestamp: new Date().toISOString()
    });
};

export const trackActivityCompleted = (activityId, activityType, unitNumber) => {
    trackEvent('activity_completed', {
        activity_id: activityId, // e.g., 'twi_m1_activity_scramble1'
        activity_type: activityType, // e.g., 'wordScramble'
        unit_number: unitNumber,
        timestamp: new Date().toISOString()
    });
};

export const trackQuizStarted = (quizId, unitNumber) => {
    trackEvent('quiz_started', {
        quiz_id: quizId, // e.g., 'twi_m1_quiz1_greetings'
        unit_number: unitNumber,
        timestamp: new Date().toISOString()
    });
};

export const trackQuizCompleted = (quizId, score, totalQuestions, unitNumber) => {
    trackEvent('quiz_completed', {
        quiz_id: quizId, // e.g., 'twi_m1_quiz1_greetings'
        score,
        total_questions: totalQuestions,
        unit_number: unitNumber,
        timestamp: new Date().toISOString()
    });
};

export const trackPracticeSessionStarted = (wordCount) => {
    trackEvent('practice_session_started', {
        word_count: wordCount,
        timestamp: new Date().toISOString()
    });
};

export const trackPracticeWordMastered = (word) => {
    trackEvent('practice_word_mastered', {
        word, // e.g., 'Akwaaba'
        timestamp: new Date().toISOString()
    });
};

// C. Engagement & Retention
export const trackSessionStarted = () => {
    trackEvent('session_started', {
        timestamp: new Date().toISOString()
    });
};

export const trackStorybookOpened = (storyId) => {
    trackEvent('storybook_opened', {
        story_id: storyId, // e.g., 'ananse_and_the_pot_of_wisdom'
        timestamp: new Date().toISOString()
    });
};

export const trackStorybookCompleted = (storyId) => {
    trackEvent('storybook_completed', {
        story_id: storyId, // e.g., 'ananse_and_the_pot_of_wisdom'
        timestamp: new Date().toISOString()
    });
};

export const trackStickerUnlocked = (unitNumber, language) => {
    trackEvent('sticker_unlocked', {
        unit_number: unitNumber,
        language, // 'Twi' | 'Yoruba'
        timestamp: new Date().toISOString()
    });
};

// D. Monetization Funnel
export const trackSubscriptionPromptViewed = (source) => {
    trackEvent('subscription_prompt_viewed', {
        source, // 'locked_lesson' | 'storybook_upsell' | 'trial_expired_modal'
        timestamp: new Date().toISOString()
    });
};

export const trackSubscriptionPageViewed = () => {
    trackEvent('subscription_page_viewed', {
        timestamp: new Date().toISOString()
    });
};

export const trackSubscriptionInitiated = (planSelected) => {
    trackEvent('subscription_initiated', {
        plan_selected: planSelected, // 'yearly' | 'monthly'
        timestamp: new Date().toISOString()
    });
};

export const trackSubscriptionSuccessful = (planSelected) => {
    trackEvent('subscription_successful', {
        plan_selected: planSelected, // 'yearly' | 'monthly'
        timestamp: new Date().toISOString()
    });
};

// E. App Health & Performance
export const trackOfflineModeActivated = () => {
    trackEvent('offline_mode_activated', {
        timestamp: new Date().toISOString()
    });
};

export const trackProgressQueueSynced = (itemsSynced) => {
    trackEvent('progress_queue_synced', {
        items_synced: itemsSynced,
        timestamp: new Date().toISOString()
    });
};

// Trial & Conversion Analytics
export const trackTrialStarted = (profileId, language, ageGroup) => {
    trackEvent('trial_started', {
        profile_id: profileId,
        language: language,
        age_group: ageGroup,
        timestamp: new Date().toISOString()
    });
};

export const trackTrialCliffhangerReached = (profileId, language, unitNumber, nextItemTitle) => {
    trackEvent('trial_cliffhanger_reached', {
        profile_id: profileId,
        language: language,
        unit_number: unitNumber,
        next_item_title: nextItemTitle,
        timestamp: new Date().toISOString()
    });
};

export const trackTrialStoryCompleted = (profileId, language, storyId) => {
    trackEvent('trial_story_completed', {
        profile_id: profileId,
        language: language,
        story_id: storyId,
        timestamp: new Date().toISOString()
    });
};

export const trackGameZoneLimitReached = (profileId, language, playsUsed) => {
    trackEvent('game_zone_limit_reached', {
        profile_id: profileId,
        language: language,
        plays_used: playsUsed,
        timestamp: new Date().toISOString()
    });
};

export const trackSubscriptionPromptDismissed = (promptType) => {
    trackEvent('subscription_prompt_dismissed', {
        prompt_type: promptType,
        timestamp: new Date().toISOString()
    });
};

// Email & Communication Analytics
export const trackEmailPreferencesUpdated = (preferences) => {
    trackEvent('email_preferences_updated', {
        promotions_enabled: preferences.promotions,
        reports_frequency: preferences.reports, // 'weekly' | 'monthly' | 'off'
        timestamp: new Date().toISOString()
    });
};

export const trackEmailSent = (emailType, userId) => {
    trackEvent('email_sent', {
        email_type: emailType, // 'progress_report' | 're_engagement' | 'family_digest'
        user_id: userId,
        timestamp: new Date().toISOString()
    });
};

export const trackEmailOpened = (emailType, userId) => {
    trackEvent('email_opened', {
        email_type: emailType,
        user_id: userId,
        timestamp: new Date().toISOString()
    });
};

export const trackEmailClicked = (emailType, userId, linkType) => {
    trackEvent('email_clicked', {
        email_type: emailType,
        user_id: userId,
        link_type: linkType, // 'continue_learning' | 'resume_learning' | 'other'
        timestamp: new Date().toISOString()
    });
};


export default {
    // Onboarding & Acquisition
    trackOnboardingStarted,
    trackLanguageSelected,
    trackTrialActivated,
    trackProfileCreated,
    
    // Core Learning Loop
    trackLessonStarted,
    trackLessonCompleted,
    trackActivityStarted,
    trackActivityCompleted,
    trackQuizStarted,
    trackQuizCompleted,
    trackPracticeSessionStarted,
    trackPracticeWordMastered,
    
    // Engagement & Retention
    trackSessionStarted,
    trackStorybookOpened,
    trackStorybookCompleted,
    trackStickerUnlocked,
    
    // Trial & Conversion
    trackTrialStarted,
    trackTrialCliffhangerReached,
    trackTrialStoryCompleted,
    trackGameZoneLimitReached,
    trackSubscriptionPromptDismissed,
    
    // Email & Communication
    trackEmailPreferencesUpdated,
    trackEmailSent,
    trackEmailOpened,
    trackEmailClicked,
    
    // Monetization
    trackSubscriptionPromptViewed,
    trackSubscriptionPageViewed,
    trackSubscriptionInitiated,
    trackSubscriptionSuccessful,
    
    // App Health
    trackOfflineModeActivated,
    trackProgressQueueSynced
};


